import axios, { AxiosRequestConfig } from 'axios'
import type { ContractBufferExceptionEvidence } from './_models'
import { appendCrewOwnershipToFormData } from './crewOwnership'
import { Crew, CrewDocument, CrewCertification, Vessel } from './_models'
import { Voyage } from '../../operations/core/_models'
import { Rank } from './_models'
import { Company, CompanyAdmin, HistoryRecord, BulkUploadResultDto, BulkUploadJob } from './_models'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'

const API_URL = process.env.REACT_APP_API_URL
const CREW_API_URL = `${API_URL}/crew`
const RANKS_API = `${API_URL}/ranks`
const COMPANY_ADMIN_API_URL = `${API_URL}/company-group-admins`
const GET_COMPANY_API_URL = `${API_URL}/users/company-admins`
const VESSEL_API_URL = `${process.env.REACT_APP_API_URL}/vessels` // Assuming this is the endpoint for vessels

export const getCrewList = async (filters?: {
  period?: string
  companyGroupId?: number
  companyAdminId?: number
  vesselId?: number
}): Promise<Crew[]> => {
  try {
    const response = await axios.get(CREW_API_URL + '/all', {
      params: {
        period: filters?.period,
        companyGroupId: filters?.companyGroupId,
        companyAdminId: filters?.companyAdminId,
        vesselId: filters?.vesselId,
      },
    })
    console.log('finalcrew:', response.data)

    return response.data
  } catch (error: any) {
    console.error('Error fetching crew list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Crew list')
  }
}

/** Crew assigned during the selected allotment month, including completed sign-offs. */
export const getAllotmentPeriodRoster = async (period: string, filters?: {
  companyGroupId?: number
  companyAdminId?: number
  vesselId?: number
}): Promise<Crew[]> => {
  const response = await axios.get<Crew[]>(`${API_URL}/crew/allotments/roster`, {
    params: {period, ...filters},
  })
  return response.data
}

export const getCrewListForDropdown = async (): Promise<Crew[]> => {
  try {
    const response = await axios.get(`${CREW_API_URL}/all/drop-down`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching crew dropdown list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching crew dropdown list')
  }
}

export interface CrewSignatureResponse {
  exists: boolean
  dataUrl: string | null
}

export const getCrewSignature = async (crewId: number): Promise<CrewSignatureResponse> => {
  const res = await axios.get<CrewSignatureResponse>(`${CREW_API_URL}/${crewId}/signature`)
  return res.data
}

export const saveCrewSignature = async (
  crewId: number,
  dataUrl: string
): Promise<CrewSignatureResponse> => {
  const res = await axios.post<CrewSignatureResponse>(`${CREW_API_URL}/${crewId}/signature`, {
    dataUrl,
  })
  return res.data
}

export const deleteCrewSignature = async (crewId: number): Promise<void> => {
  await axios.delete(`${CREW_API_URL}/${crewId}/signature`)
}

const formatLocalDate = (date: Date) => date.toISOString().split('T')[0] // "YYYY-MM-DD"
const formatLocalDateTime = (date: Date) => date.toISOString().split('.')[0]

function fmtDateTime(dt: Date) {
  // "2025-07-06T18:30:00"
  return dt.toISOString().split('.')[0]
}

export const createCrew = async (
  name: string,
  dateOfBirth: Date,
  dateOfJoining: string,
  nationality: string,
  department: string,
  contactNumber: string,
  email: string,
  username: string,
  password: string,
  rankId: number,
  companyGroupAdminId: number,
  companyAdminId: number | null,
  // vesselId: number,
  // startDate: Date,
  // endDate: Date,
  certifications: { documentName: string; file: File | null }[],
  documents: { documentName: string; file: File | null }[],
  employeeId?: string
): Promise<Crew[]> => {
  const formData = new FormData()

  // —––– Primitive fields map 1:1 to your CrewDto properties —–––
  formData.append('name', name)
  formData.append('username', username)
  formData.append('nationality', nationality)
  formData.append('department', department)
  formData.append('contactNumber', contactNumber)
  formData.append('email', email)
  formData.append('password', password)
  formData.append('rankId', rankId.toString())
  appendCrewOwnershipToFormData(formData, {
    companyGroupAdminId,
    companyAdminId: companyAdminId != null && companyAdminId > 0 ? companyAdminId : null,
  })
  // formData.append('vessel.id', vesselId.toString());
  formData.append('dateOfBirth', formatLocalDate(dateOfBirth))
  // formData.append('startDate', formatLocalDateTime(startDate));
  // formData.append('endDate', formatLocalDateTime(endDate));
  formData.append('isActive', 'true') // or dto.isActive
  formData.append('dateOfJoining', dateOfJoining || '')
  if (employeeId) {
    formData.append('employeeId', employeeId)
  }


  // —––– Now append your two parallel lists in lock-step —–––
  certifications.forEach((cert, idx) => {
    formData.append('certificationNames', cert.documentName)
    if (cert.file) {
      formData.append('certificationFiles', cert.file)
    }
  })

  documents.forEach((doc, idx) => {
    formData.append('documentNames', doc.documentName)
    if (doc.file) {
      formData.append('documentFiles', doc.file)
    }
  })

  const response = await axios.post(`${CREW_API_URL}/create`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

export const updateCrew = async (
  crewId: number,
  crewDto: {
    name: string
    dateOfBirth: string // "YYYY-MM-DD"
    dateOfJoining?: string
    nationality: string
    department: string
    contactNumber: string
    email?: string
    rankId: number
    // vessel: { id: number };
    companyGroupAdminId: number
    active: boolean
    // startDate: Date;
    // endDate: Date;
    notes?: string
  }
): Promise<Crew[]> => {
  try {
    const form = new FormData()
    form.append('name', crewDto.name)
    form.append('dateOfBirth', crewDto.dateOfBirth)
    form.append('dateOfJoining', crewDto.dateOfJoining || '')
    form.append('nationality', crewDto.nationality)
    form.append('department', crewDto.department)
    form.append('contactNumber', crewDto.contactNumber)
    form.append('email', crewDto.email || '')
    form.append('rankId', crewDto.rankId.toString())
    // this is the one you were missing
    // form.append('vessel.id', String(crewDto.vessel.id));
    form.append('companyGroupAdminId', String(crewDto.companyGroupAdminId))
    form.append('active', String(crewDto.active))
    // form.append('startDate', fmtDateTime(crewDto.startDate));
    // form.append('endDate',   fmtDateTime(crewDto.endDate));
    form.append('notes', crewDto.notes || '')

    const response = await axios.put(`${CREW_API_URL}/update/${crewId}`, form)

    return response.data
  } catch (error: any) {
    console.error('Error updating crew:', error)
    throw new Error(error.response?.data?.message || 'Error updating crew')
  }
}

// New API to update the active status of a crew member
export const updateCrewStatus = async (crewId: number, active: boolean): Promise<Crew> => {
  try {
    const response = await axios.put(`${CREW_API_URL}/status-update/${crewId}`, { active })
    return response.data
  } catch (error: any) {
    console.error('Error updating crew status:', error)
    throw new Error(error.response?.data?.message || 'Error updating crew status')
  }
}

export const updateCrewApprovalStatus = async (
  crewId: number,
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
): Promise<Crew> => {
  try {
    const response = await axios.put(`${CREW_API_URL}/${crewId}/approval`, null, {
      params: { status },
    })
    return response.data
  } catch (error: any) {
    console.error('Error updating crew approval status:', error)
    throw new Error(error.response?.data?.message || 'Error updating crew approval status')
  }
}

export const deleteVessel = async (crewId: number): Promise<Crew[]> => {
  try {
    const response = await axios.delete(CREW_API_URL + '/delete/' + crewId)

    return response.data
  } catch (error: any) {
    console.error('Error deleting crew item:', error)
    throw new Error(error.response?.data?.message || 'Error deleting crew item')
  }
}

// —–––– Bulk Upload APIs ––––

export const previewCrewUpload = async (
  file: File
): Promise<BulkUploadResultDto> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post(`${CREW_API_URL}/upload/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const startCrewUploadJob = async (
  file: File
): Promise<BulkUploadJob> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post(`${CREW_API_URL}/upload/async`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const fetchCrewUploadJob = async (jobId: number): Promise<BulkUploadJob> => {
  const response = await axios.get(`${CREW_API_URL}/upload/job/${jobId}`)
  return response.data
}

export const fetchActiveCrewUploadJob = async (): Promise<BulkUploadJob | null> => {
  const response = await axios.get(`${CREW_API_URL}/upload/job/active`)
  return response.data
}

export const downloadCrewTemplate = async (): Promise<Blob> => {
  const response = await axios.get(`${CREW_API_URL}/upload/template`, {
    responseType: 'blob',
  })
  return new Blob([response.data])
}

export const cancelCrewUploadJob = async (jobId: number): Promise<void> => {
  await axios.post(`${CREW_API_URL}/upload/job/${jobId}/cancel`)
}

// —–––– Vessels APIs ––––

export const getVesselList = async (): Promise<Vessel[]> => {
  try {
    const response = await axios.get(VESSEL_API_URL, {
      headers: {},
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching vessel list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel list')
  }
}

// —–––– Documents APIs ––––

export const getCrewDocuments = async (crewId: number): Promise<CrewDocument[]> => {
  const res = await axios.get(`${API_URL}/documents/${crewId}`)
  return (res.data as any[]).map((doc) => ({
    id: doc.id,
    documentName: doc.documentName,
    // point at the new `/view` endpoint
    fileUrl: `${API_URL}/documents/${crewId}/${doc.id}/view`,
  }))
}

export const uploadCrewDocument = async (
  crewId: number,
  documentName: string,
  file: File
): Promise<CrewDocument[]> => {
  const form = new FormData()
  form.append('documentNames', documentName)
  // <-- send under the key the backend is expecting:
  form.append('file', file)

  const res = await axios.post(`${API_URL}/documents/${crewId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export const deleteCrewDocument = async (crewId: number, docId: number): Promise<void> => {
  await axios.delete(`${API_URL}/documents/${crewId}/${docId}`)
}

// —–––– Certifications APIs ––––

export const getCrewCertifications = async (crewId: number): Promise<CrewCertification[]> => {
  const res = await axios.get(`${API_URL}/certifications/${crewId}`)
  return (res.data as any[]).map((cert) => ({
    id: cert.id,
    documentName: cert.certificationName,
    // point at the new `/view` endpoint
    fileUrl: `${API_URL}/certifications/${crewId}/${cert.id}/view`,
  }))
}

export const uploadCrewCertification = async (
  crewId: number,
  documentName: string,
  file: File
): Promise<CrewCertification[]> => {
  const form = new FormData()
  form.append('certificationNames', documentName)
  // <-- same here:
  form.append('file', file)

  const res = await axios.post(`${API_URL}/certifications/${crewId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export const deleteCrewCertification = async (crewId: number, certId: number): Promise<void> => {
  await axios.delete(`${API_URL}/certifications/${crewId}/${certId}`)
}

/**
 * Fetch all ranks so we can look up rankName by rankId.
 */
export const getRanks = async (): Promise<Rank[]> => {
  const res = await axios.get<Rank[]>(RANKS_API + '/drop-down')
  return res.data
}

export const getRanksforList = async (): Promise<Rank[]> => {
  const res = await axios.get<Rank[]>(RANKS_API)
  return res.data
}

export const getUniqueRanks = async (): Promise<Rank[]> => {
  const res = await axios.get<Rank[]>(RANKS_API)
  const seenRanks = new Set<string>();
  return res.data.filter(r => {
    const rankName = r.rank ?? '';
    if (seenRanks.has(rankName)) return false;
    seenRanks.add(rankName);
    return true;
  });
}

/**
 * (optional) Fetch a single rank by id.
 */
export const getRankById = async (id: number): Promise<Rank> => {
  const res = await axios.get<Rank>(`${RANKS_API}/drop-down/${id}`)
  return res.data
}

export interface SignOnRequest {
  contractBufferException?: boolean
  contractBufferExceptionReason?: string
  vesselId: number
  crewId: number
  signOnDate: string // "YYYY-MM-DD"
  dateOfAgreement?: string
  contractEndDate?: string
  signOnTime?: string // "HH:mm" (optional)
  portSignOn: string
}

export interface SignOnResponse extends ContractBufferExceptionEvidence {
  id: number
  vesselId: number
  vesselName: string
  crewId: number
  crewName: string
  rank: number
  signOnDate: string
  signOnTime?: string | null
  signOffDate: string | null
  signOffReason: string | null
  portSignOn: string
  portSignOff: string | null
  travelArrangements: string | null
  status: string // "SIGNED_ON" etc
}

/**
 * Sign a crew on to a vessel.
 */
export const signOnCrew = async (payload: SignOnRequest): Promise<SignOnResponse> => {
  console.log('payload: ', payload)
  const res = await axios.post<SignOnResponse>(`${API_URL}/sign/on`, payload)
  return res.data
}

// at the bottom of core/_requests.ts

export interface SignOnOffRecord extends ContractBufferExceptionEvidence {
  id: number
  vesselId: number
  vesselName: string
  crewId: number
  crewName: string
  rank: number // this is the rankId
  rankInfo?: { id: number; rank: string } | null
  signOnDate: string // "YYYY-MM-DD"
  dateOfAgreement?: string | null
  signOnTime?: string | null
  signOffDate: string | null
  signOffReason: string | null
  portSignOn: string
  portSignOff: string | null
  travelArrangements: string | null
  status: 'SIGNED_ON' | 'SIGNED_OFF' | string
  contractEndDate?: string | null
}

/**
 * Fetch all sign-on / sign-off records.
 */
export const getSignOnOffRecords = async (): Promise<SignOnOffRecord[]> => {
  const res = await axios.get<SignOnOffRecord[]>(`${API_URL}/sign/on-off-records/all`)
  console.log('Signonof recs: ', res.data)
  return res.data
}

// at bottom of core/_requests.ts

export interface SignOffRequest {
  recordId: number
  signOffDate: string // "YYYY-MM-DD"
  signOffReason: string
  portSignOff: string
}

export interface SignOffResponse extends SignOnOffRecord { }

export const signOffCrew = async (payload: SignOffRequest): Promise<SignOffResponse> => {
  const res = await axios.post<SignOffResponse>(`${API_URL}/sign/off`, payload)
  return res.data
}

// ============== CREW ALLOTMENT API ==============

export interface CrewAllotmentRecord {
  allotmentId: number
  pbCrewId?: number | null
  crewId?: number | null
  beneficiaryName?: string | null
  bankAccount?: string | null
  bankName?: string | null
  amount?: number | null
  carryForwardAmount?: number | null
  wagesEarned?: number | null
  allotmentAmount?: number | null
  balanceBroughtForward?: number | null
  remarks?: string | null
  statementMonth?: string | null
  workflowStatus?: 'DRAFT' | 'PENDING_MASTER_REVIEW' | 'MASTER_REVIEWED' | 'REVIEWED' | 'SUBMITTED' | string | null
  officeRemarks?: string | null
  masterRemarks?: string | null
  submittedAt?: string | null
  masterReviewedAt?: string | null
  reviewedAt?: string | null
  attachments?: CrewAllotmentAttachment[] | null
  attachmentCount?: number | null
}

export interface CrewBankingDetailsRecord {
  id?: number
  crewId?: number
  nameOfBeneficiary?: string | null
  accountNumber?: string | null
  bankName?: string | null
  branchName?: string | null
  branchCode?: string | null
  ifscRtgsType?: 'IFSC' | 'RTGS' | string | null
  ifscRtgsCode?: string | null
  swiftIandi?: string | null
  bankAddress?: string | null
  accountCurrency?: 'USD' | 'EUR' | 'GBP' | 'SGD' | 'INR' | 'AED' | 'OTHER' | string | null
  otherCurrency?: string | null
  salaryCurrency?: 'USD' | 'EUR' | 'GBP' | 'SGD' | 'INR' | 'AED' | 'OTHER' | string | null
  salaryOtherCurrency?: string | null
  remarks?: string | null
  basicWages?: number | null
  fixedOvertime109Hrs?: number | null
  leaveWages9DaysPerMonth?: number | null
  subSmb?: number | null
  tradeAllowance?: number | null
  otherAllowance?: number | null
  ownersLoyaltyBonus?: number | null
  rejoiningBonus?: number | null
  totalBasic?: number | null
  leavePay?: number | null
  overtimePay?: number | null
  overtimeRate?: number | null
  incentive?: number | null
  cashAdvanceFromOffice?: number | null
  deductionBond?: number | null
  deductionInternetCard?: number | null
  otherDeductions?: number | null
  deductionsTotal?: number | null
  salary?: number | null
}

export interface CrewAllotmentAttachment {
  id: number
  fileName: string
  filePath?: string | null
  fileType?: string | null
  fileSize?: number | null
  mimeType?: string | null
}

export const getCrewAllotments = async (
  crewId: number,
  period?: string
): Promise<CrewAllotmentRecord[]> => {
  const res = await axios.get<CrewAllotmentRecord[]>(`${API_URL}/crew/${crewId}/allotments`, {
    params: {
      period,
    },
  })
  return res.data
}

export interface CrewAllotmentOverviewRecord {
  crewId: number
  allotments: CrewAllotmentRecord[]
  previousPeriodAllotments: CrewAllotmentRecord[]
  bankingDetails: CrewBankingDetailsRecord[]
  allotmentAccessDenied?: boolean
  canManageAllotmentDraft?: boolean
}

export const getCrewAllotmentOverview = async (
  crewIds: number[],
  period: string,
  previousPeriod?: string
): Promise<CrewAllotmentOverviewRecord[]> => {
  const chunks: number[][] = []
  const uniqueCrewIds = Array.from(new Set(crewIds))
  for (let index = 0; index < uniqueCrewIds.length; index += 200) {
    chunks.push(uniqueCrewIds.slice(index, index + 200))
  }

  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const response = await axios.post<CrewAllotmentOverviewRecord[]>(
          `${API_URL}/crew/allotments/overview`,
          { crewIds: chunk, period }
        )
        const rows = response.data
        if (
          !Array.isArray(rows) ||
          rows.length !== chunk.length ||
          new Set(rows.map((row) => Number(row.crewId))).size !== chunk.length ||
          rows.some((row) => !chunk.includes(Number(row.crewId)))
        ) {
          throw new Error('Incomplete crew allotment overview response')
        }
        // The old page still fetched banking details for crew whose allotment read was denied.
        // Preserve that behavior through the existing banking endpoint for those rows only.
        return Promise.all(
          rows.map(async (row) =>
            row.allotmentAccessDenied
              ? { ...row, bankingDetails: await getCrewBankingDetails(row.crewId) }
              : row
          )
        )
      } catch (error) {
        const status = (error as any)?.response?.status
        if (status === 401 || status === 403) throw error
        // Support rolling deployments by retaining the original per-crew read path.
        return Promise.all(
          chunk.map(async (crewId) => {
            const [allotments, bankingDetails, previousPeriodAllotments] = await Promise.all([
              getCrewAllotments(crewId, period).catch(() => [] as CrewAllotmentRecord[]),
              getCrewBankingDetails(crewId),
              previousPeriod
                ? getCrewAllotments(crewId, previousPeriod).catch(
                    () => [] as CrewAllotmentRecord[]
                  )
                : Promise.resolve([] as CrewAllotmentRecord[]),
            ])
            return { crewId, allotments, previousPeriodAllotments, bankingDetails }
          })
        )
      }
    })
  )

  return pages.flat()
}

export const createCrewAllotment = async (
  crewId: number,
  payload: CrewAllotmentRecord
): Promise<CrewAllotmentRecord> => {
  const res = await axios.post<CrewAllotmentRecord>(`${API_URL}/crew/${crewId}/allotments`, payload)
  return res.data
}

export const updateCrewAllotment = async (
  crewId: number,
  allotmentId: number,
  payload: CrewAllotmentRecord
): Promise<CrewAllotmentRecord> => {
  const res = await axios.put<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}`,
    payload
  )
  return res.data
}

export const submitCrewAllotment = async (
  crewId: number,
  allotmentId: number
): Promise<CrewAllotmentRecord> => {
  const res = await axios.post<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}/submit`
  )
  return res.data
}

export const saveCrewAllotmentMasterReviewNote = async (
  crewId: number,
  allotmentId: number,
  masterRemarks: string
): Promise<CrewAllotmentRecord> => {
  const res = await axios.post<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}/master-review-note`,
    { officeRemarks: masterRemarks }
  )
  return res.data
}

export const approveCrewAllotmentMasterReview = async (
  crewId: number,
  allotmentId: number,
  masterRemarks: string
): Promise<CrewAllotmentRecord> => {
  const res = await axios.post<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}/master-approve`,
    { officeRemarks: masterRemarks }
  )
  return res.data
}

export const saveCrewAllotmentReviewNote = async (
  crewId: number,
  allotmentId: number,
  officeRemarks: string
): Promise<CrewAllotmentRecord> => {
  const res = await axios.post<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}/review-note`,
    { officeRemarks }
  )
  return res.data
}

export const closeCrewAllotmentSatisfactory = async (
  crewId: number,
  allotmentId: number,
  officeRemarks: string
): Promise<CrewAllotmentRecord> => {
  const res = await axios.post<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}/close-satisfactory`,
    { officeRemarks }
  )
  return res.data
}

export const bulkReviewAllotments = async (
  period: string,
  vesselId: number | undefined,
  officeRemarks: string
): Promise<CrewAllotmentRecord[]> => {
  const res = await axios.post<CrewAllotmentRecord[]>(
    `${API_URL}/allotments/bulk-review`,
    { officeRemarks },
    {
      params: {
        period,
        vesselId: vesselId || undefined,
      },
    }
  )
  return res.data
}


export const bulkMasterReviewCrewClaims = async (
  period: string,
  vesselId: number | undefined,
  masterRemarks: string,
  file: File | null | undefined
): Promise<CrewClaim[]> => {
  const formData = new FormData()
  formData.append('period', period)
  if (vesselId) formData.append('vesselId', String(vesselId))
  formData.append('masterRemarks', masterRemarks)
  if (file) formData.append('file', file)
  const res = await axios.post<CrewClaim[]>(
    `${API_URL}/crew-claims/bulk-master-review`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return res.data
}

export const bulkMasterReviewAllotments = async (
  period: string,
  vesselId: number | undefined,
  masterRemarks: string,
  file: File | null | undefined
): Promise<CrewAllotmentRecord[]> => {
  const formData = new FormData()
  formData.append('period', period)
  if (vesselId) formData.append('vesselId', String(vesselId))
  formData.append('masterRemarks', masterRemarks)
  if (file) formData.append('file', file)
  const res = await axios.post<CrewAllotmentRecord[]>(
    `${API_URL}/allotments/bulk-master-review`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return res.data
}

export const uploadCrewAllotmentAttachment = async (
  crewId: number,
  allotmentId: number,
  file: File
): Promise<CrewAllotmentRecord> => {
  const formData = new FormData()
  formData.append('file', file)
  const res = await axios.post<CrewAllotmentRecord>(
    `${API_URL}/crew/${crewId}/allotments/${allotmentId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return res.data
}

/**
 * Get the view URL for a crew allotment attachment
 */
export const crewAllotmentAttachmentViewUrl = (
  crewId: number,
  allotmentId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew/${crewId}/allotments/${allotmentId}/attachments/${attachmentId}/view`
}

/**
 * Get the download URL for a crew allotment attachment
 */
export const crewAllotmentAttachmentDownloadUrl = (
  crewId: number,
  allotmentId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew/${crewId}/allotments/${allotmentId}/attachments/${attachmentId}/download`
}

export const getCompanyList = async (): Promise<Company[]> => {
  try {
    const response = await axios.get(GET_COMPANY_API_URL)

    return response.data
  } catch (error: any) {
    console.error('Error fetching company list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company list')
  }
}

export const getCompanyAdminList = async (): Promise<CompanyAdmin[]> => {
  try {
    const response = await axios.get(COMPANY_ADMIN_API_URL)

    return response.data // Return company data
  } catch (error: any) {
    console.error('Error fetching company admin list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company admin list')
  }
}

// at the bottom, after signOffCrew...
export interface UpdateAssignmentPayload {
  contractBufferException?: boolean
  contractBufferExceptionReason?: string
  signOnDate?: string
  dateOfAgreement?: string
  contractEndDate?: string
  signOnTime?: string
  portSignOn: string
  signOffDate?: string
}

/**
 * Update a sign-on/off record (only PLANNED or SIGNED_ON allowed).
 */
export const updateAssignment = async (
  recordId: number,
  payload: UpdateAssignmentPayload
): Promise<SignOnOffRecord> => {
  const res = await axios.put<SignOnOffRecord>(`${API_URL}/sign/update/${recordId}`, payload)
  return res.data
}

/**
 * Fetch change‑history for one sign‑on/off record.
 */
export const getHistory = async (recordId: number): Promise<HistoryRecord[]> => {
  const res = await axios.get<HistoryRecord | HistoryRecord[]>(
    `${API_URL}/sign-on-records/${recordId}/history`
  )
  const data = res.data
  // normalize to array
  return Array.isArray(data) ? data : [data]
}

// ============== CREW CLAIMS API ==============

export type ClaimStatus = 'DRAFT' | 'MASTER_REVIEW_PENDING' | 'SUBMITTED' | 'REVIEWED' | 'REJECTED'

export interface CrewClaimAttachment {
  id: number
  fileName: string
  filePath: string
  fileType: string
  fileSize: number
}

export interface CrewClaim {
  canManageFinancialDraft?: boolean
  id: number
  srNo: number
  crewId: number
  crewName: string
  vesselId: number | null
  vesselName: string | null
  rankId: number | null
  rankName: string | null
  claimMonth: string
  salary: number
  medical: number
  safetyGear: number
  travel: number
  boardingLodging: number
  hraAllowance: number
  bonus: number
  miscReimbursements: number
  overtimeAmount?: number | null
  totalAmount: number
  bfAmount?: number | null
  wagesEarned?: number | null
  allotmentAmount?: number | null
  cashAdvance?: number | null
  balanceCf?: number | null
  unionSocialDues?: string | null
  deductionCashAdvanceFromOffice?: number | null
  deductionBond?: number | null
  deductionOtherDeductions?: number | null
  deductionInternetCard?: number | null
  firstTimeDeduction?: number | null
  deductionsTotal?: number | null
  remarks: string
  officeRemarks?: string | null
  status: ClaimStatus
  submittedAt: string | null
  reviewedAt: string | null
  reviewedByName: string | null
  rejectionReason: string | null
  accountCurrency?: string | null
  otherCurrency?: string | null
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  attachments: CrewClaimAttachment[]
  attachmentCount: number
  createdAt: string
  updatedAt: string
}

export interface CreateCrewClaimRequest {
  crewId?: number
  vesselId?: number
  rankId?: number
  claimMonth?: string
  salary?: number
  medical?: number
  safetyGear?: number
  travel?: number
  boardingLodging?: number
  hraAllowance?: number
  bonus?: number
  miscReimbursements?: number
  overtimeAmount?: number
  bfAmount?: number
  wagesEarned?: number
  allotmentAmount?: number
  cashAdvance?: number
  balanceCf?: number
  unionSocialDues?: string
  deductionCashAdvanceFromOffice?: number
  deductionBond?: number
  deductionOtherDeductions?: number
  deductionInternetCard?: number
  firstTimeDeduction?: number
  deductionsTotal?: number
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  remarks?: string
}

export interface UpdateCrewClaimRequest {
  vesselId?: number
  rankId?: number
  claimMonth?: string
  salary?: number
  medical?: number
  safetyGear?: number
  travel?: number
  boardingLodging?: number
  hraAllowance?: number
  bonus?: number
  miscReimbursements?: number
  overtimeAmount?: number
  bfAmount?: number
  wagesEarned?: number
  allotmentAmount?: number
  cashAdvance?: number
  balanceCf?: number
  unionSocialDues?: string
  deductionCashAdvanceFromOffice?: number
  deductionBond?: number
  deductionOtherDeductions?: number
  deductionInternetCard?: number
  firstTimeDeduction?: number
  deductionsTotal?: number
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  remarks?: string
  officeRemarks?: string
}

export const getCrewBankingDetails = async (
  crewId: number
): Promise<CrewBankingDetailsRecord[]> => {
  if (!crewId) return []

  try {
    const res = await axios.get<CrewBankingDetailsRecord[] | CrewBankingDetailsRecord>(
      `${API_URL}/crew/${crewId}/banking-details`
    )

    const rows = Array.isArray(res.data) ? res.data : res.data ? [res.data] : []

    return [...rows].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0)).slice(0, 3)
  } catch (error) {
    console.error('Error fetching crew banking details list:', error)
    return []
  }
}

export const createCrewBankingDetails = async (
  crewId: number,
  payload: CrewBankingDetailsRecord
): Promise<CrewBankingDetailsRecord> => {
  const res = await axios.post<CrewBankingDetailsRecord>(
    `${API_URL}/crew/${crewId}/banking-details/create`,
    payload
  )
  return res.data
}

export const updateCrewBankingDetails = async (
  crewId: number,
  bankingDetailsId: number,
  payload: CrewBankingDetailsRecord
): Promise<CrewBankingDetailsRecord> => {
  const res = await axios.put<CrewBankingDetailsRecord>(
    `${API_URL}/crew/${crewId}/banking-details/update/${bankingDetailsId}`,
    payload
  )
  return res.data
}

interface CrewBankingDetailsSalaryResponse {
  id?: number
  salary?: number | null
}

const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const salaryComponentKeys: Array<keyof CrewBankingDetailsRecord> = [
  'salary',
  'basicWages',
  'fixedOvertime109Hrs',
  'leaveWages9DaysPerMonth',
  'subSmb',
  'tradeAllowance',
  'otherAllowance',
  'ownersLoyaltyBonus',
  'rejoiningBonus',
  'totalBasic',
  'leavePay',
  'overtimePay',
  'overtimeRate',
  'incentive',
]

const hasAnySalaryPayload = (row?: CrewBankingDetailsRecord | null): boolean => {
  if (!row) return false
  return salaryComponentKeys.some((key) => row[key] != null)
}

const deriveSalaryFromBankingRow = (row?: CrewBankingDetailsRecord | null): number => {
  if (!row) return 0

  const explicitSalary = toFiniteNumberOrNull(row.salary)
  if (explicitSalary != null) {
    return explicitSalary
  }

  const newBreakdownKeys: Array<keyof CrewBankingDetailsRecord> = [
    'basicWages',
    'fixedOvertime109Hrs',
    'leaveWages9DaysPerMonth',
    'subSmb',
    'tradeAllowance',
    'otherAllowance',
    'ownersLoyaltyBonus',
    'rejoiningBonus',
  ]
  const legacyBreakdownKeys: Array<keyof CrewBankingDetailsRecord> = [
    'totalBasic',
    'leavePay',
    'overtimePay',
    'incentive',
  ]

  const hasNewBreakdown = newBreakdownKeys.some((key) => row[key] != null)
  if (hasNewBreakdown) {
    return newBreakdownKeys.reduce((sum, key) => sum + (toFiniteNumberOrNull(row[key]) ?? 0), 0)
  }

  const hasLegacyBreakdown = legacyBreakdownKeys.some((key) => row[key] != null)
  if (hasLegacyBreakdown) {
    return legacyBreakdownKeys.reduce((sum, key) => sum + (toFiniteNumberOrNull(row[key]) ?? 0), 0)
  }

  return 0
}

const mergeLatestAccountWithSalarySource = (
  latestAccount: CrewBankingDetailsRecord,
  salarySource: CrewBankingDetailsRecord | null
): CrewBankingDetailsRecord => {
  if (!salarySource || salarySource.id === latestAccount.id) {
    return latestAccount
  }

  const merged: CrewBankingDetailsRecord = { ...latestAccount }
  salaryComponentKeys.forEach((key) => {
    const latestValue = (latestAccount as any)[key]
    if (latestValue != null) {
      return
    }

    const sourceValue = (salarySource as any)[key]
    if (sourceValue != null) {
      ; (merged as any)[key] = sourceValue
    }
  })

  // Prefer the first positive overtime rate from salary-bearing records.
  const mergedOvertimeRate = toFiniteNumberOrNull(merged.overtimeRate)
  const sourceOvertimeRate = toFiniteNumberOrNull(salarySource.overtimeRate)
  if ((mergedOvertimeRate == null || mergedOvertimeRate <= 0) && sourceOvertimeRate != null) {
    merged.overtimeRate = sourceOvertimeRate
  }

  if (merged.salary == null) {
    merged.salary = deriveSalaryFromBankingRow(merged)
  }

  return merged
}

export const getCrewLatestBankingDetails = async (
  crewId: number
): Promise<CrewBankingDetailsRecord | null> => {
  if (!crewId) return null

  try {
    const res = await axios.get<CrewBankingDetailsRecord[] | CrewBankingDetailsRecord>(
      `${API_URL}/crew/${crewId}/banking-details`
    )

    const rows = Array.isArray(res.data) ? res.data : res.data ? [res.data] : []

    if (rows.length === 0) return null

    const sorted = [...rows].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
    const latestAccount = sorted[0] ?? null
    if (!latestAccount) return null

    const salarySource = sorted.find((row) => hasAnySalaryPayload(row)) ?? null
    return mergeLatestAccountWithSalarySource(latestAccount, salarySource)
  } catch (error) {
    console.error('Error fetching crew banking details:', error)
    return null
  }
}

export const getCrewBankingSalary = async (crewId: number): Promise<number> => {
  if (!crewId) return 0

  try {
    const res = await axios.get<
      CrewBankingDetailsSalaryResponse[] | CrewBankingDetailsSalaryResponse
    >(`${API_URL}/crew/${crewId}/banking-details`)

    const rows = Array.isArray(res.data) ? res.data : res.data ? [res.data] : []

    if (rows.length === 0) return 0

    const sorted = [...rows].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
    const salarySource = sorted.find((row) => hasAnySalaryPayload(row)) ?? sorted[0] ?? null
    const salary = deriveSalaryFromBankingRow(salarySource)
    return Number.isFinite(salary) ? salary : 0
  } catch (error) {
    console.error('Error fetching crew banking salary:', error)
    return 0
  }
}

/**
 * Fetch all crew claims (filtered by user role)
 */
export const getCrewClaims = async (): Promise<CrewClaim[]> => {
  const res = await axios.get<CrewClaim[]>(`${API_URL}/crew-claims/all`)
  return res.data
}

/**
 * Fetch crew claims by status
 */
export const getCrewClaimsByStatus = async (status: ClaimStatus): Promise<CrewClaim[]> => {
  const res = await axios.get<CrewClaim[]>(`${API_URL}/crew-claims/status/${status}`)
  return res.data
}

/**
 * Fetch crew claims by month
 */
export const getCrewClaimsByMonth = async (month: string): Promise<CrewClaim[]> => {
  const res = await axios.get<CrewClaim[]>(`${API_URL}/crew-claims/month`, {
    params: { month },
  })
  return res.data
}

/**
 * Fetch a single crew claim by ID
 */
export const getCrewClaimById = async (id: number): Promise<CrewClaim> => {
  const res = await axios.get<CrewClaim>(`${API_URL}/crew-claims/${id}`)
  return res.data
}

/**
 * Create a new crew claim
 */
export const createCrewClaim = async (request: CreateCrewClaimRequest): Promise<CrewClaim> => {
  const res = await axios.post<CrewClaim>(`${API_URL}/crew-claims/create`, request)
  return res.data
}

/**
 * Update an existing crew claim
 */
export const updateCrewClaim = async (
  id: number,
  request: UpdateCrewClaimRequest
): Promise<CrewClaim> => {
  const res = await axios.put<CrewClaim>(`${API_URL}/crew-claims/update/${id}`, request)
  return res.data
}

/**
 * Submit a crew claim for review (DRAFT -> SUBMITTED)
 */
export const submitCrewClaim = async (id: number): Promise<CrewClaim> => {
  const res = await axios.post<CrewClaim>(`${API_URL}/crew-claims/${id}/submit`)
  return res.data
}

export const saveCrewClaimMasterReview = async (
  id: number, masterRemarks: string, approve: boolean
): Promise<CrewClaim> => {
  const action = approve ? 'master-approve' : 'master-review-note'
  const res = await axios.post<CrewClaim>(`${API_URL}/crew-claims/${id}/${action}`, {masterRemarks})
  return res.data
}

/**
 * Review/Approve a crew claim (SUBMITTED -> REVIEWED)
 */
export const reviewCrewClaim = async (id: number): Promise<CrewClaim> => {
  const res = await axios.post<CrewClaim>(`${API_URL}/crew-claims/${id}/review`)
  return res.data
}

/**
 * Reject a crew claim (SUBMITTED -> REJECTED)
 */
export const rejectCrewClaim = async (id: number, reason: string): Promise<CrewClaim> => {
  const res = await axios.post<CrewClaim>(`${API_URL}/crew-claims/${id}/reject`, { reason })
  return res.data
}

/**
 * Delete a crew claim (only DRAFT claims)
 */
export const deleteCrewClaim = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/crew-claims/${id}`)
}

/**
 * Upload attachment to a crew claim
 */
export const uploadCrewClaimAttachment = async (
  claimId: number,
  file: File
): Promise<CrewClaim> => {
  const form = new FormData()
  form.append('file', file)
  const res = await axios.post<CrewClaim>(`${API_URL}/crew-claims/${claimId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

/**
 * Delete an attachment from a crew claim
 */
export const deleteCrewClaimAttachment = async (
  claimId: number,
  attachmentId: number
): Promise<CrewClaim> => {
  const res = await axios.delete<CrewClaim>(
    `${API_URL}/crew-claims/${claimId}/attachments/${attachmentId}`
  )
  return res.data
}

/**
 * Get the view URL for a crew claim attachment
 */
export const crewClaimAttachmentViewUrl = (claimId: number, attachmentId: number): string => {
  return `${API_URL}/crew-claims/${claimId}/attachments/${attachmentId}/view`
}

/**
 * Get the download URL for a crew claim attachment
 */
export const crewClaimAttachmentDownloadUrl = (claimId: number, attachmentId: number): string => {
  return `${API_URL}/crew-claims/${claimId}/attachments/${attachmentId}/download`
}

// ============== CREW DEDUCTIONS API ==============

export type DeductionStatus = 'DRAFT' | 'SUBMITTED' | 'REVIEWED' | 'REJECTED'

export interface CrewDeductionAttachment {
  id: number
  fileName: string
  filePath: string
  fileType: string
  fileSize: number
}

export interface CrewDeduction {
  canManageFinancialDraft?: boolean
  id: number
  srNo: number
  crewId: number
  crewName: string
  vesselId: number | null
  vesselName: string | null
  rankId: number | null
  rankName: string | null
  claimMonth: string
  salary: number
  medical: number
  safetyGear: number
  travel: number
  boardingLodging: number
  hraAllowance: number
  bonus: number
  miscReimbursements: number
  totalAmount: number
  bfAmount?: number | null
  wagesEarned?: number | null
  allotmentAmount?: number | null
  cashAdvance?: number | null
  balanceCf?: number | null
  unionSocialDues?: string | null
  deductionCashAdvanceFromOffice?: number | null
  deductionBond?: number | null
  deductionOtherDeductions?: number | null
  deductionInternetCard?: number | null
  firstTimeDeduction?: number | null
  deductionsTotal?: number | null
  remarks: string
  officeRemarks?: string | null
  status: DeductionStatus
  submittedAt: string | null
  reviewedAt: string | null
  reviewedByName: string | null
  rejectionReason: string | null
  accountCurrency?: string | null
  otherCurrency?: string | null
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  attachments: CrewDeductionAttachment[]
  attachmentCount: number
  createdAt: string
  updatedAt: string
}

export interface CreateCrewDeductionRequest {
  crewId?: number
  vesselId?: number
  rankId?: number
  claimMonth?: string
  unionSocialDues?: string
  deductionCashAdvanceFromOffice?: number
  deductionBond?: number
  deductionOtherDeductions?: number
  deductionInternetCard?: number
  firstTimeDeduction?: number
  deductionsTotal?: number
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  remarks?: string
}

export interface UpdateCrewDeductionRequest {
  vesselId?: number
  rankId?: number
  claimMonth?: string
  unionSocialDues?: string
  deductionCashAdvanceFromOffice?: number
  deductionBond?: number
  deductionOtherDeductions?: number
  deductionInternetCard?: number
  firstTimeDeduction?: number
  deductionsTotal?: number
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  remarks?: string
  officeRemarks?: string
}

export const getCrewDeductions = async (): Promise<CrewDeduction[]> => {
  const res = await axios.get<CrewDeduction[]>(`${API_URL}/crew-deductions/all`)
  return res.data
}

export const getCrewDeductionsByStatus = async (
  status: DeductionStatus
): Promise<CrewDeduction[]> => {
  const res = await axios.get<CrewDeduction[]>(`${API_URL}/crew-deductions/status/${status}`)
  return res.data
}

export const getCrewDeductionsByMonth = async (month: string): Promise<CrewDeduction[]> => {
  const res = await axios.get<CrewDeduction[]>(`${API_URL}/crew-deductions/month`, {
    params: { month },
  })
  return res.data
}

export const getCrewDeductionById = async (id: number): Promise<CrewDeduction> => {
  const res = await axios.get<CrewDeduction>(`${API_URL}/crew-deductions/${id}`)
  return res.data
}

export const createCrewDeduction = async (
  request: CreateCrewDeductionRequest
): Promise<CrewDeduction> => {
  const res = await axios.post<CrewDeduction>(`${API_URL}/crew-deductions/create`, request)
  return res.data
}

export const updateCrewDeduction = async (
  id: number,
  request: UpdateCrewDeductionRequest
): Promise<CrewDeduction> => {
  const res = await axios.put<CrewDeduction>(`${API_URL}/crew-deductions/update/${id}`, request)
  return res.data
}

export const submitCrewDeduction = async (id: number): Promise<CrewDeduction> => {
  const res = await axios.post<CrewDeduction>(`${API_URL}/crew-deductions/${id}/submit`)
  return res.data
}

export const reviewCrewDeduction = async (id: number): Promise<CrewDeduction> => {
  const res = await axios.post<CrewDeduction>(`${API_URL}/crew-deductions/${id}/review`)
  return res.data
}

export const rejectCrewDeduction = async (id: number, reason: string): Promise<CrewDeduction> => {
  const res = await axios.post<CrewDeduction>(`${API_URL}/crew-deductions/${id}/reject`, { reason })
  return res.data
}

export const deleteCrewDeduction = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/crew-deductions/${id}`)
}

export const uploadCrewDeductionAttachment = async (
  deductionId: number,
  file: File
): Promise<CrewDeduction> => {
  const form = new FormData()
  form.append('file', file)
  const res = await axios.post<CrewDeduction>(
    `${API_URL}/crew-deductions/${deductionId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return res.data
}

export const deleteCrewDeductionAttachment = async (
  deductionId: number,
  attachmentId: number
): Promise<CrewDeduction> => {
  const res = await axios.delete<CrewDeduction>(
    `${API_URL}/crew-deductions/${deductionId}/attachments/${attachmentId}`
  )
  return res.data
}

export const crewDeductionAttachmentViewUrl = (
  deductionId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-deductions/${deductionId}/attachments/${attachmentId}/view`
}

export const crewDeductionAttachmentDownloadUrl = (
  deductionId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-deductions/${deductionId}/attachments/${attachmentId}/download`
}

// ============== CREW COMPLAINTS API ==============

export type ComplaintStatus = 'DRAFT' | 'SUBMITTED' | 'REVIEWED' | 'REJECTED'
export type ComplaintWorkflowStage =
  | 'HOD_REVIEW'
  | 'MASTER_REVIEW'
  | 'OFFICE_REVIEW'
  | 'AUTHORITIES_REVIEW'
  | 'CLOSED'
  | string

export interface CrewComplaintAttachment {
  id: number
  fileName: string
  filePath: string
  fileType: string
  fileSize: number
}

export interface CrewComplaint {
  id: number
  srNo: number
  crewId: number
  crewName: string
  vesselId: number | null
  vesselName: string | null
  rankId: number | null
  rankName: string | null
  recordDate: string
  reportDate: string
  seafarerName: string
  complaintType?: string | null
  description: string
  hodMasterResponse?: string | null
  hodMasterResponseDate?: string | null
  hodResponse?: string | null
  hodResponseDate?: string | null
  masterResponse?: string | null
  masterResponseDate?: string | null
  onboardPreventRecurrence?: string | null
  hodSignature?: string | null
  masterSignature?: string | null
  onboardReviewRole?: 'HOD' | 'MASTER' | string | null
  assignedHodRole?: 'CHIEF_OFFICER' | 'CHIEF_ENGINEER' | string | null
  onboardResolved?: boolean | null
  onboardReviewedBy?: string | null
  onboardReviewedAt?: string | null
  workflowStage?: ComplaintWorkflowStage | null
  authoritiesReminderAt?: string | null
  officeActionTaken?: string | null
  officeActionDate?: string | null
  officeResolved?: boolean | null
  officePreventRecurrence?: string | null
  officeWelfareOfficerSignature?: string | null
  officeResolutionDate?: string | null
  authoritiesActionTaken?: string | null
  authoritiesActionDate?: string | null
  authoritiesPreventRecurrence?: string | null
  authoritiesWelfareOfficerSignature?: string | null
  authoritiesResolutionDate?: string | null
  officeRemarks?: string | null
  status: ComplaintStatus
  submittedAt: string | null
  reviewedAt: string | null
  reviewedByName: string | null
  rejectionReason: string | null
  attachments: CrewComplaintAttachment[]
  attachmentCount: number
  createdAt: string
  updatedAt: string
}

export interface CreateCrewComplaintRequest {
  crewId?: number
  vesselId?: number
  rankId?: number
  recordDate?: string
  reportDate?: string
  seafarerName?: string
  complaintType?: string
  description: string
  hodMasterResponse?: string
  hodMasterResponseDate?: string
  onboardPreventRecurrence?: string
  hodSignature?: string
  masterSignature?: string
  onboardReviewRole?: 'HOD' | 'MASTER' | string
  assignedHodRole?: 'CHIEF_OFFICER' | 'CHIEF_ENGINEER' | string
  onboardResolved?: boolean
  officeResolved?: boolean
}

export interface UpdateCrewComplaintRequest {
  vesselId?: number
  rankId?: number
  recordDate?: string
  reportDate?: string
  seafarerName?: string
  complaintType?: string
  description?: string
  hodMasterResponse?: string
  hodMasterResponseDate?: string
  onboardPreventRecurrence?: string
  hodSignature?: string
  masterSignature?: string
  onboardReviewRole?: 'HOD' | 'MASTER' | string
  assignedHodRole?: 'CHIEF_OFFICER' | 'CHIEF_ENGINEER' | string
  onboardResolved?: boolean
  officeResolved?: boolean
  officeActionTaken?: string
  officeActionDate?: string
  officePreventRecurrence?: string
  officeWelfareOfficerSignature?: string
  officeResolutionDate?: string
  authoritiesActionTaken?: string
  authoritiesActionDate?: string
  authoritiesPreventRecurrence?: string
  authoritiesWelfareOfficerSignature?: string
  authoritiesResolutionDate?: string
  officeRemarks?: string
}

const normalizeCrewComplaint = (item: any): CrewComplaint => {
  const complaintType =
    item?.complaintType ??
    item?.complaintTypeName ??
    item?.complaint_type ??
    item?.type_of_complaint ??
    item?.typeOfComplaint ??
    item?.complaint_type_name ??
    null

  return {
    ...item,
    complaintType,
  } as CrewComplaint
}

export const getCrewComplaints = async (): Promise<CrewComplaint[]> => {
  const res = await axios.get<CrewComplaint[]>(`${API_URL}/crew-complaints/all`)
  return (res.data || []).map((item: any) => normalizeCrewComplaint(item))
}

export const getCrewComplaintsByStatus = async (
  status: ComplaintStatus
): Promise<CrewComplaint[]> => {
  const res = await axios.get<CrewComplaint[]>(`${API_URL}/crew-complaints/status/${status}`)
  return (res.data || []).map((item: any) => normalizeCrewComplaint(item))
}

export const getCrewComplaintsByMonth = async (month: string): Promise<CrewComplaint[]> => {
  const res = await axios.get<CrewComplaint[]>(`${API_URL}/crew-complaints/month`, {
    params: { month },
  })
  return (res.data || []).map((item: any) => normalizeCrewComplaint(item))
}

export const getCrewComplaintById = async (id: number): Promise<CrewComplaint> => {
  const res = await axios.get<CrewComplaint>(`${API_URL}/crew-complaints/${id}`)
  return normalizeCrewComplaint(res.data)
}

export const createCrewComplaint = async (
  request: CreateCrewComplaintRequest
): Promise<CrewComplaint> => {
  const payload = {
    ...request,
    complaint_type: request.complaintType,
  }
  const res = await axios.post<CrewComplaint>(`${API_URL}/crew-complaints/create`, payload)
  return normalizeCrewComplaint(res.data)
}

export const updateCrewComplaint = async (
  id: number,
  request: UpdateCrewComplaintRequest
): Promise<CrewComplaint> => {
  const payload = {
    ...request,
    complaint_type: request.complaintType,
  }
  const res = await axios.put<CrewComplaint>(`${API_URL}/crew-complaints/update/${id}`, payload)
  return normalizeCrewComplaint(res.data)
}

export const submitCrewComplaint = async (id: number): Promise<CrewComplaint> => {
  const res = await axios.post<CrewComplaint>(`${API_URL}/crew-complaints/${id}/submit`)
  return res.data
}

export const reviewCrewComplaint = async (id: number): Promise<CrewComplaint> => {
  const res = await axios.post<CrewComplaint>(`${API_URL}/crew-complaints/${id}/review`)
  return res.data
}

export const rejectCrewComplaint = async (id: number, reason: string): Promise<CrewComplaint> => {
  const res = await axios.post<CrewComplaint>(`${API_URL}/crew-complaints/${id}/reject`, { reason })
  return res.data
}

export const deleteCrewComplaint = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/crew-complaints/${id}`)
}

export const uploadCrewComplaintAttachment = async (
  complaintId: number,
  file: File
): Promise<CrewComplaint> => {
  const form = new FormData()
  form.append('file', file)
  const res = await axios.post<CrewComplaint>(
    `${API_URL}/crew-complaints/${complaintId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return res.data
}

export const deleteCrewComplaintAttachment = async (
  complaintId: number,
  attachmentId: number
): Promise<CrewComplaint> => {
  const res = await axios.delete<CrewComplaint>(
    `${API_URL}/crew-complaints/${complaintId}/attachments/${attachmentId}`
  )
  return res.data
}

export const crewComplaintAttachmentViewUrl = (
  complaintId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-complaints/${complaintId}/attachments/${attachmentId}/view`
}

export const crewComplaintAttachmentDownloadUrl = (
  complaintId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-complaints/${complaintId}/attachments/${attachmentId}/download`
}

// ============== CREW OVERTIME API ==============

export type OvertimeStatus = 'DRAFT' | 'SUBMITTED' | 'REVIEWED' | 'REJECTED'
export type OvertimeWorkflowStage = 'MASTER_REVIEW' | 'OFFICE_REVIEW' | 'CLOSED'
export type OvertimeDayWiseSlots = Record<string, boolean[]>

export interface CrewOvertime {
  id: number
  srNo: number
  serialNo: string
  crewId: number
  crewName: string
  vesselId: number | null
  vesselName: string | null
  rankId: number | null
  rankName: string | null
  overtimeMonth: string
  otHours: number
  maxOtAllowed: number
  basicWages: number
  accountCurrency?: string | null
  otherCurrency?: string | null
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  overtimeRate?: number
  otPerMonth: number
  otPerHour: number
  otAmount: number
  exceedAmount: number
  remarks?: string | null
  dayWiseSlots?: OvertimeDayWiseSlots | null
  officeRemarks?: string | null
  workflowStage?: OvertimeWorkflowStage | null
  status: OvertimeStatus
  submittedAt?: string | null
  reviewedAt?: string | null
  reviewedByName?: string | null
  rejectionReason?: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateCrewOvertimeRequest {
  serialNo?: string
  crewId?: number
  vesselId?: number
  rankId?: number
  overtimeMonth?: string
  otHours?: number
  maxOtAllowed?: number
  basicWages?: number
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  overtimeRate?: number
  dayWiseSlots?: OvertimeDayWiseSlots
  remarks?: string
}

export interface UpdateCrewOvertimeRequest {
  serialNo?: string
  vesselId?: number
  rankId?: number
  overtimeMonth?: string
  otHours?: number
  maxOtAllowed?: number
  basicWages?: number
  salaryCurrency?: string | null
  salaryOtherCurrency?: string | null
  overtimeRate?: number
  dayWiseSlots?: OvertimeDayWiseSlots
  remarks?: string
  officeRemarks?: string
}

export const getCrewOvertimes = async (): Promise<CrewOvertime[]> => {
  const res = await axios.get<CrewOvertime[]>(`${API_URL}/crew-overtimes/all`)
  return res.data
}

export const getCrewOvertimeById = async (id: number): Promise<CrewOvertime> => {
  const res = await axios.get<CrewOvertime>(`${API_URL}/crew-overtimes/${id}`)
  return res.data
}

export const createCrewOvertime = async (
  request: CreateCrewOvertimeRequest
): Promise<CrewOvertime> => {
  const res = await axios.post<CrewOvertime>(`${API_URL}/crew-overtimes/create`, request)
  return res.data
}

export const updateCrewOvertime = async (
  id: number,
  request: UpdateCrewOvertimeRequest
): Promise<CrewOvertime> => {
  const res = await axios.put<CrewOvertime>(`${API_URL}/crew-overtimes/update/${id}`, request)
  return res.data
}

export const submitCrewOvertime = async (id: number): Promise<CrewOvertime> => {
  const res = await axios.post<CrewOvertime>(`${API_URL}/crew-overtimes/${id}/submit`)
  return res.data
}

export const reviewCrewOvertime = async (id: number): Promise<CrewOvertime> => {
  const res = await axios.post<CrewOvertime>(`${API_URL}/crew-overtimes/${id}/review`)
  return res.data
}

export const rejectCrewOvertime = async (id: number, reason: string): Promise<CrewOvertime> => {
  const res = await axios.post<CrewOvertime>(`${API_URL}/crew-overtimes/${id}/reject`, { reason })
  return res.data
}

export const deleteCrewOvertime = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/crew-overtimes/${id}`)
}

// ============== CREW APPRAISAL API ==============

export type AppraisalWorkflowStatus = 'DRAFT' | 'SUBMITTED' | 'REVIEWED'
export type AppraisalType = 'MID_TERM' | 'HOD_SIGNOFF' | 'SPECIAL_CASE'
export type AverageReport = 'NTBE' | 'AVERAGE' | 'GOOD' | 'EXCELLENT'
export type ReviewDepartment = 'OPERATION' | 'TECHNICAL' | 'QHSC' | 'CREWING'
export type AppraisalAttachmentType =
  | 'APPRAISAL_RATINGS'
  | 'APPRAISAL_OFFICERS_ENGINEERS'
  | 'APPRAISAL_MASTER_CHIEF_ENGINEER'

export interface CrewAppraisalAttachment {
  id: number
  attachmentType: AppraisalAttachmentType
  fileName: string
  filePath?: string | null
  fileType?: string | null
  fileSize?: number | null
}

export interface CrewAppraisal {
  id: number
  srNo: number
  crewId: number
  crewName: string
  vesselId: number | null
  vesselName: string | null
  rankId: number | null
  rankName: string | null
  appraisalType: AppraisalType
  averageReport: AverageReport
  remarks?: string | null
  officeRemarks?: string | null

  // Department reviews
  operationRemarks?: string | null
  operationRating?: number | null
  operationReviewedAt?: string | null
  operationReviewedByName?: string | null

  technicalRemarks?: string | null
  technicalRating?: number | null
  technicalReviewedAt?: string | null
  technicalReviewedByName?: string | null

  qhscRemarks?: string | null
  qhscRating?: number | null
  qhscReviewedAt?: string | null
  qhscReviewedByName?: string | null

  crewingRemarks?: string | null
  crewingRating?: number | null
  crewingReviewedAt?: string | null
  crewingReviewedByName?: string | null

  reviewCount?: number

  workflowStatus: AppraisalWorkflowStatus
  submittedAt?: string | null
  reviewedAt?: string | null
  reviewedByName?: string | null
  createdByUserId?: number | null
  createdByUsername?: string | null
  attachments: CrewAppraisalAttachment[]
  attachmentCount: number
  createdAt: string
  updatedAt: string
}

export interface CreateCrewAppraisalRequest {
  crewId?: number
  vesselId?: number
  rankId?: number
  appraisalType?: AppraisalType
  averageReport?: AverageReport
  remarks?: string
}

export interface UpdateCrewAppraisalRequest {
  vesselId?: number
  rankId?: number
  appraisalType?: AppraisalType
  averageReport?: AverageReport
  remarks?: string
  officeRemarks?: string
}

export interface DepartmentReviewRequest {
  department: ReviewDepartment
  remarks: string
  rating: number // 1-10
}

export const getCrewAppraisals = async (): Promise<CrewAppraisal[]> => {
  const res = await axios.get<CrewAppraisal[]>(`${API_URL}/crew-appraisals/all`)
  return res.data
}

export const getCrewAppraisalsByStatus = async (
  status: AppraisalWorkflowStatus
): Promise<CrewAppraisal[]> => {
  const res = await axios.get<CrewAppraisal[]>(`${API_URL}/crew-appraisals/status/${status}`)
  return res.data
}

export const getCrewAppraisalById = async (id: number): Promise<CrewAppraisal> => {
  const res = await axios.get<CrewAppraisal>(`${API_URL}/crew-appraisals/${id}`)
  return res.data
}

export const createCrewAppraisal = async (
  request: CreateCrewAppraisalRequest
): Promise<CrewAppraisal> => {
  const res = await axios.post<CrewAppraisal>(`${API_URL}/crew-appraisals/create`, request)
  return res.data
}

export const updateCrewAppraisal = async (
  id: number,
  request: UpdateCrewAppraisalRequest
): Promise<CrewAppraisal> => {
  const res = await axios.put<CrewAppraisal>(`${API_URL}/crew-appraisals/update/${id}`, request)
  return res.data
}

export const submitCrewAppraisal = async (id: number): Promise<CrewAppraisal> => {
  const res = await axios.post<CrewAppraisal>(`${API_URL}/crew-appraisals/${id}/submit`)
  return res.data
}

export const reviewCrewAppraisal = async (id: number): Promise<CrewAppraisal> => {
  const res = await axios.post<CrewAppraisal>(`${API_URL}/crew-appraisals/${id}/review`)
  return res.data
}

export const submitDepartmentReview = async (
  id: number,
  request: DepartmentReviewRequest
): Promise<CrewAppraisal> => {
  const res = await axios.post<CrewAppraisal>(
    `${API_URL}/crew-appraisals/${id}/department-review`,
    request
  )
  return res.data
}

export const deleteCrewAppraisal = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/crew-appraisals/${id}`)
}

export const uploadCrewAppraisalAttachment = async (
  appraisalId: number,
  attachmentType: AppraisalAttachmentType,
  file: File
): Promise<CrewAppraisal> => {
  const form = new FormData()
  form.append('attachmentType', attachmentType)
  form.append('file', file)
  const res = await axios.post<CrewAppraisal>(
    `${API_URL}/crew-appraisals/${appraisalId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return res.data
}

export const deleteCrewAppraisalAttachment = async (
  appraisalId: number,
  attachmentId: number
): Promise<CrewAppraisal> => {
  const res = await axios.delete<CrewAppraisal>(
    `${API_URL}/crew-appraisals/${appraisalId}/attachments/${attachmentId}`
  )
  return res.data
}

export const crewAppraisalAttachmentViewUrl = (
  appraisalId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-appraisals/${appraisalId}/attachments/${attachmentId}/view`
}

export const crewAppraisalAttachmentDownloadUrl = (
  appraisalId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-appraisals/${appraisalId}/attachments/${attachmentId}/download`
}

// ============== CREW TRAINING API ==============

export type CrewTrainingWorkflowStatus = 'DRAFT' | 'SUBMITTED' | 'REVIEWED' | 'REJECTED'

export type CrewTrainingType =
  | 'PRE_JOINING_VALUE_ADDED'
  | 'PRE_JOINING_SHIP_SPECIFIC'
  | 'COMPANY_SPECIFIC'
  | 'ONBOARD_TRAINING'
  | 'ON_LEAVE_TRAINING'
  | 'OTHER_VALUE_ADDED'

export type CrewTrainingMode = 'REMOTE' | 'PHYSICAL' | 'VIDEO'

export interface CrewTrainingAttachment {
  id: number
  fileName: string
  filePath?: string | null
  fileType?: string | null
  fileSize?: number | null
}

export interface CrewTraining {
  id: number
  srNo: number
  crewId: number
  crewName: string
  vesselId: number | null
  vesselName: string | null
  rankId: number | null
  rankName: string | null
  trainingType: CrewTrainingType
  topic: string
  mode: CrewTrainingMode
  remarks?: string | null
  officeRemarks?: string | null
  workflowStatus: CrewTrainingWorkflowStatus
  submittedAt?: string | null
  reviewedAt?: string | null
  rejectedAt?: string | null
  reviewedByName?: string | null
  rejectionReason?: string | null
  attachments: CrewTrainingAttachment[]
  attachmentCount: number
  createdAt: string
  updatedAt: string
}

export interface CreateCrewTrainingRequest {
  crewId?: number
  vesselId?: number
  rankId?: number
  trainingType?: CrewTrainingType
  topic?: string
  mode?: CrewTrainingMode
  remarks?: string
}

export interface UpdateCrewTrainingRequest {
  vesselId?: number
  rankId?: number
  trainingType?: CrewTrainingType
  topic?: string
  mode?: CrewTrainingMode
  remarks?: string
  officeRemarks?: string
}

export const getCrewTrainings = async (): Promise<CrewTraining[]> => {
  try {
    const res = await axios.get<CrewTraining[]>(`${API_URL}/crew-trainings/all`)
    return res.data
  } catch (error: any) {
    console.error('Error fetching crew trainings:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to fetch crew training records', 'load'))
  }
}

export const getCrewTrainingsByStatus = async (
  status: CrewTrainingWorkflowStatus
): Promise<CrewTraining[]> => {
  try {
    const res = await axios.get<CrewTraining[]>(`${API_URL}/crew-trainings/status/${status}`)
    return res.data
  } catch (error: any) {
    console.error('Error fetching crew trainings by status:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to fetch crew training records by status', 'load'))
  }
}

export const getCrewTrainingById = async (id: number): Promise<CrewTraining> => {
  try {
    const res = await axios.get<CrewTraining>(`${API_URL}/crew-trainings/${id}`)
    return res.data
  } catch (error: any) {
    console.error(`Error fetching crew training ${id}:`, error)
    throw new Error(getApiErrorMessage(error, `Failed to fetch crew training #${id}`, 'load'))
  }
}

export const createCrewTraining = async (
  request: CreateCrewTrainingRequest
): Promise<CrewTraining> => {
  try {
    const res = await axios.post<CrewTraining>(`${API_URL}/crew-trainings/create`, request)
    return res.data
  } catch (error: any) {
    console.error('Error creating crew training:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to create crew training record', 'create'))
  }
}

export const updateCrewTraining = async (
  id: number,
  request: UpdateCrewTrainingRequest
): Promise<CrewTraining> => {
  try {
    const res = await axios.put<CrewTraining>(`${API_URL}/crew-trainings/update/${id}`, request)
    return res.data
  } catch (error: any) {
    console.error(`Error updating crew training ${id}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to update crew training record', 'update'))
  }
}

export const submitCrewTraining = async (id: number): Promise<CrewTraining> => {
  try {
    const res = await axios.post<CrewTraining>(`${API_URL}/crew-trainings/${id}/submit`)
    return res.data
  } catch (error: any) {
    console.error(`Error submitting crew training ${id}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to submit crew training record', 'save'))
  }
}

export const reviewCrewTraining = async (id: number): Promise<CrewTraining> => {
  try {
    const res = await axios.post<CrewTraining>(`${API_URL}/crew-trainings/${id}/review`)
    return res.data
  } catch (error: any) {
    console.error(`Error reviewing crew training ${id}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to review crew training record', 'save'))
  }
}

export const rejectCrewTraining = async (id: number, reason: string): Promise<CrewTraining> => {
  try {
    const res = await axios.post<CrewTraining>(`${API_URL}/crew-trainings/${id}/reject`, { reason })
    return res.data
  } catch (error: any) {
    console.error(`Error rejecting crew training ${id}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to reject crew training record', 'save'))
  }
}

export const deleteCrewTraining = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/crew-trainings/${id}`)
  } catch (error: any) {
    console.error(`Error deleting crew training ${id}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to delete crew training record', 'delete'))
  }
}

export const uploadCrewTrainingAttachment = async (
  trainingId: number,
  file: File
): Promise<CrewTraining> => {
  try {
    const form = new FormData()
    form.append('file', file)
    const res = await axios.post<CrewTraining>(
      `${API_URL}/crew-trainings/${trainingId}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return res.data
  } catch (error: any) {
    console.error(`Error uploading attachment for crew training ${trainingId}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to upload training attachment', 'update'))
  }
}

export const deleteCrewTrainingAttachment = async (
  trainingId: number,
  attachmentId: number
): Promise<CrewTraining> => {
  try {
    const res = await axios.delete<CrewTraining>(
      `${API_URL}/crew-trainings/${trainingId}/attachments/${attachmentId}`
    )
    return res.data
  } catch (error: any) {
    console.error(`Error deleting attachment ${attachmentId} for crew training ${trainingId}:`, error)
    throw new Error(getApiErrorMessage(error, 'Failed to remove attachment', 'delete'))
  }
}

export const crewTrainingAttachmentViewUrl = (trainingId: number, attachmentId: number): string => {
  return `${API_URL}/crew-trainings/${trainingId}/attachments/${attachmentId}/view`
}

export const crewTrainingAttachmentDownloadUrl = (
  trainingId: number,
  attachmentId: number
): string => {
  return `${API_URL}/crew-trainings/${trainingId}/attachments/${attachmentId}/download`
}

// ──────────────── DASHBOARD HELPERS ────────────────

export interface CrewDashboardSummary {
  totalCrew: number
  activeCrew: number
  inactiveCrew: number
  pendingApprovals: number
  onboardNow: number
  expiredCertifications: number
  certificationsExpiringSoon: number // within 30 days
  crewByDepartment: { department: string; count: number }[]
  crewByRank: { rankId: number; rankName: string; count: number }[]
  crewByVessel: { vesselId: number; vesselName: string; count: number }[]
  recentSignOnOff: SignOnOffRecord[]
}

export const getCrewDashboardSummary = (
  crewList: Crew[],
  signOnOffRecords: SignOnOffRecord[],
  ranks: Rank[]
): CrewDashboardSummary => {
  const now = new Date()
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)

  const totalCrew = crewList.length
  const activeCrew = crewList.filter((c) => c.active).length
  const inactiveCrew = crewList.filter((c) => !c.active).length
  const pendingApprovals = crewList.filter(
    (c) => c.approvalStatus === 'PENDING' || c.approvalStatus === undefined || c.approvalStatus === null
  ).length

  const onboardNow = signOnOffRecords.filter((r) => r.status === 'SIGNED_ON').length

  // Certifications (client-side heuristic: track expiry from documents)
  const certificationsExpiringSoon = crewList.filter((c) => {
    const docs = [...(c.certifications || []), ...(c.documents || [])]
    return docs.some((d) => d.documentName && d.documentName.toLowerCase().includes('expir'))
  }).length
  const expiredCertifications = 0 // No expiry date on current model

  // Crew by department
  const deptMap: Record<string, number> = {}
  crewList.forEach((c) => {
    const dept = c.department || 'Unknown'
    deptMap[dept] = (deptMap[dept] || 0) + 1
  })
  const crewByDepartment = Object.entries(deptMap).map(([department, count]) => ({ department, count }))

  // Crew by rank
  const rankMap: Record<string, { rankId: number; count: number }> = {}
  crewList.forEach((c) => {
    const key = `rank_${c.rankId}`
    if (!rankMap[key]) {
      rankMap[key] = { rankId: c.rankId, count: 0 }
    }
    rankMap[key].count++
  })
  const rankLookup = new Map(ranks.map((r) => [r.id, r.rank]))
  const crewByRank = Object.values(rankMap).map((r) => ({
    rankId: r.rankId,
    rankName: rankLookup.get(r.rankId) || `Rank #${r.rankId}`,
    count: r.count,
  }))

  // Crew by vessel
  const vesselMap: Record<string, { vesselId: number; vesselName: string; count: number }> = {}
  crewList.forEach((c) => {
    const vId = c.vessel?.id || c.vesselId || 0
    const vName = c.vessel?.fleet_name || c.vesselName || 'Unassigned'
    const key = `vessel_${vId}`
    if (!vesselMap[key]) {
      vesselMap[key] = { vesselId: vId, vesselName: vName, count: 0 }
    }
    vesselMap[key].count++
  })
  const crewByVessel = Object.values(vesselMap)

  // Recent sign-on/off (last 10)
  const recentSignOnOff = [...signOnOffRecords]
    .sort((a, b) => new Date(b.signOnDate).getTime() - new Date(a.signOnDate).getTime())
    .slice(0, 10)

  return {
    totalCrew,
    activeCrew,
    inactiveCrew,
    pendingApprovals,
    onboardNow,
    expiredCertifications,
    certificationsExpiringSoon,
    crewByDepartment,
    crewByRank,
    crewByVessel,
    recentSignOnOff,
  }
}
